import { useEffect, useState, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { redirects } from "@/data/redirects";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { routes } from "@/data/routes";
import { Layout } from "@/components/Layout";
import { Meta } from "@/components/Meta";
import {
  HomePage, ServiceDetail, ServiceHub, PricingPage, FaqPage, PrivacyPage, TermsPage,
  WorkPage, ProjectPage, ReviewsPage, AboutPage, ContactPage, ResourcesPage, GuidesPage,
  BlogPage, ResourceArticle, LegacyPostPage, CheckoutPage, CheckoutSuccessPage,
  CheckoutCancelPage, StartPage, MarketingFitPage, StubPage, HudsonChat, OnSiteContentPage,
  FreeContentIdeasPage, SpeakingPage,
} from "@/pageRegistry";

const PAGE_BY_PATH = {
  "/checkout": CheckoutPage,
  "/checkout/success": CheckoutSuccessPage,
  "/checkout/cancel": CheckoutCancelPage,
  "/start": StartPage,
  "/marketing-fit": MarketingFitPage,
  "/pricing": PricingPage,
  "/faq": FaqPage,
  "/privacy": PrivacyPage,
  "/terms": TermsPage,
  "/work": WorkPage,
  "/reviews": ReviewsPage,
  "/about": AboutPage,
  "/contact": ContactPage,
  "/resources": ResourcesPage,
  "/guides": GuidesPage,
  "/blog": BlogPage,
};

function NotFound() {
  useEffect(() => {
    // Replace (not duplicate) SEO signals so the rendered DOM reads as a clean 404.
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots ? robots.content : null;
    if (robots) robots.content = "noindex, nofollow";
    const canonical = document.querySelector('link[rel="canonical"]');
    const canonicalParent = canonical ? canonical.parentNode : null;
    if (canonical) canonical.remove();
    const prevTitle = document.title;
    document.title = "Page not found | Blissbranding";
    return () => {
      if (robots && prevRobots) robots.content = prevRobots;
      if (canonical && canonicalParent) canonicalParent.appendChild(canonical);
      document.title = prevTitle;
    };
  }, []);
  return (
    <div className="bliss-section">
      <div className="bliss-container">
        <p className="bliss-mono bliss-kicker bliss-kicker-blue">404 · NOT FOUND</p>
        <h1 className="bliss-h1">Page not found</h1>
        <p className="bliss-lede">That page doesn't exist. Try the services menu or head home.</p>
        <div className="bliss-actions">
          <a href="/" className="bliss-cta bliss-cta-orange">Back to the homepage</a>
          <a href="/services" className="bliss-cta bliss-cta-outline">Browse services</a>
        </div>
      </div>
    </div>
  );
}

const elementFor = (route) => {
  if (route.path === "/") return <HomePage />;
  const Page =
    PAGE_BY_PATH[route.path] ||
    (route.template === "serviceDetail" ? ServiceDetail :
     route.template === "serviceHub" ? ServiceHub :
     route.template === "onsiteContent" ? OnSiteContentPage :
     route.template === "leadMagnet" ? FreeContentIdeasPage :
     route.template === "speaking" ? SpeakingPage :
     route.template === "clientProject" ? ProjectPage :
     route.template === "legacyPost" ? LegacyPostPage :
     StubPage);
  if (route.template === "article" || route.template === "guide") {
    return (
      <>
        <Meta route={route} />
        <ResourceArticle route={route} isGuide={route.template === "guide"} />
      </>
    );
  }
  return (
    <>
      <Meta route={route} />
      <Page route={route} />
    </>
  );
};

export default function AppShell() {
  const { pathname, hash, search } = useLocation();
  const navigate = useNavigate();
  const [mountHudson, setMountHudson] = useState(false);
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    const t = setTimeout(() => setMountHudson(true), 2500);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const norm = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
    if (redirects[norm]) {
      navigate(redirects[norm] + search + hash, { replace: true });
      return;
    }
    trackPageView(norm, routes.find((r) => r.path === norm)?.title || document.title);
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash, search, navigate]);
  return (
    <div className="App">
      <Layout>
        <Suspense fallback={null}>
          <Routes>
            {routes.map((r) => (
              <Route key={r.path} path={r.path} element={elementFor(r)} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
      {mountHudson && (
        <Suspense fallback={null}>
          <HudsonChat />
        </Suspense>
      )}
    </div>
  );
}
